import React, { PropsWithChildren } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { useWidthContext } from '../../providers/Width';

import './InformationCard.css';
import withAsyncImport from '../../withAsyncImport';

const InformationCardHomeVariant = withAsyncImport(() => import('./variants/InformationCardHomeVariant'));
const InformationCardAboutVariant = withAsyncImport(() => import('./variants/InformationCardAboutVariant'));
const InformationCardCDVariant = withAsyncImport(() => import('./variants/InformationCardCDVariant'));
const InformationCardGeneralVariant = withAsyncImport(() => import('./variants/InformationCardGeneralVariant'));

type Icon = {
    iconLabel: string;
    iconPath: string;
    iconAltText: string;
}
/* eslint-disable no-unused-vars */
enum Alignment {
    LEFT = 'left',
    CENTER = 'center'
}
/* eslint-enable no-unused-vars */
export interface IInformationCardProps extends MappedComponentProperties {
    variant?: 'home' | 'about' | 'high-yield-jumbo' | 'general';
    displayVariation?: 'useIcon' | 'useImg' | 'useNone';
    buttonVariant?: 'solid' | 'ghosted-dark' | 'ghosted-light';
    textAlignmentDesktop?: Alignment;
    textAlignmentMobile?: Alignment;
    iconAlignment?: Alignment;
    title?: string;
    subtitle?: string;
    description?: string;
    jumboVariantPercentage?: string;
    jumboVariantTerm?: string;
    buttonLabel?: string;
    buttonLink?: string;
    buttonLinkParams?: string;
    buttonDisclaimer?: string;
    iconName?: string;
    marginTop?: string;
    iconWidth?: string;
    iconHeight?: string;
    iconLink?: string;
    linkParams?: string;
    containerMarginTop?: string;
    containerMarginBottom?: string;
    containerMarginLeft?: string;
    containerMarginRight?: string;
    containerMarginTopMobile?: string;
    containerMarginBottomMobile?: string;
    containerMarginLeftMobile?: string;
    containerMarginRightMobile?: string;
    apiEnabled?: boolean;
    buttonLinkOpenNewTab?: boolean;
    iconLinkOpenNewTab?: boolean;
    icon?: Icon;
    imgSelector?: string;
    imgMarginTop?: string;
    imgBottomPadding?: string;
    alignment?: string;
    imgWidth?: string;
    imgAltText?: string;
    imgLink?: string;
    imgLinkTo?: string;
    newTab?: boolean;
}

export interface IInformationCardVariantProps extends IInformationCardProps {
    textAlignment?: Alignment;
    buttonUrl?: string | undefined;
    linkUrl?: string | undefined;
    cssClass?: string;
}

export const InformationCardEditConfig = {
    emptyLabel: 'InformationCard',
    isEmpty: (props: PropsWithChildren<IInformationCardProps>) => {
        return !props || !props.variant;
    },
};

const getActiveVariant = (props: IInformationCardProps, width: number): JSX.Element | null => {
    const textAlignment = (width <= 768) ? props.textAlignmentMobile : props.textAlignmentDesktop;
    const buttonUrl = props.buttonLink ? `${props.buttonLink}${props.buttonLinkParams || ''}`: undefined;
    const linkUrl = props.iconLink ? `${props.iconLink}${props.linkParams || ''}` : undefined;

    switch (props.variant) {
        case 'general': return (<InformationCardGeneralVariant textAlignment={textAlignment} buttonUrl={buttonUrl} {...props} />);
        case 'home': return (<InformationCardHomeVariant textAlignment={textAlignment} buttonUrl={buttonUrl} linkUrl={linkUrl} {...props} />);
        case 'about': return (<InformationCardAboutVariant textAlignment={textAlignment} buttonUrl={buttonUrl} {...props} />);
        case 'high-yield-jumbo': return (<InformationCardCDVariant buttonVariant={props.buttonVariant} textAlignment={textAlignment} buttonUrl={buttonUrl} {...props} />);
        default: return null;
    }
};

const InformationCard = (props: PropsWithChildren<IInformationCardProps>): JSX.Element => {
    const { width } = useWidthContext();
    const desktopMarginClasses = `${props.containerMarginTop || ''} ${props.containerMarginBottom || ''} ${props.containerMarginLeft || ''} ${props.containerMarginRight || ''}`;
    const mobileMarginClasses = `${props.containerMarginTopMobile || ''} ${props.containerMarginBottomMobile || ''} ${props.containerMarginLeftMobile || ''} ${props.containerMarginRightMobile || ''}`;

    return (
        <div id='information-card' className={`InformationCardComponent ${mobileMarginClasses.trim()} ${desktopMarginClasses.trim()}`}>
            {getActiveVariant(props, width)}
        </div>
    );
};

export default InformationCard;
